import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { WindowRef } from '@spartacus/core';
import { BehaviorSubject, Observable, Subscription, fromEvent, interval, merge, of } from 'rxjs';
import { filter, map, switchMap, take, throttleTime } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class InactivityService implements OnDestroy {
  readonly timeFromLastEvent$: Observable<number>;

  private static readonly TIME_TO_RELOAD = 1000 * 60 * 60 * 6;

  private readonly lastEventSubject$: BehaviorSubject<number>;
  private readonly subscription: Subscription;

  constructor(
    private window: WindowRef,
    private ngZone: NgZone,
  ) {
    this.lastEventSubject$ = new BehaviorSubject(Date.now());
    this.timeFromLastEvent$ = this.lastEventSubject$.pipe(map((lastEventTime) => Date.now() - lastEventTime));
    this.subscription = new Subscription();
  }

  init(): void {
    this.ngZone.runOutsideAngular(() => {
      this.subscription.add(
        merge(fromEvent(this.window.nativeWindow, 'scroll'), fromEvent(this.window.nativeWindow, 'click'))
          .pipe(
            throttleTime(1000),
            map(() => Date.now()),
          )
          .subscribe(this.lastEventSubject$),
      );

      this.subscription.add(
        interval(1000)
          .pipe(
            switchMap(() => this.timeFromLastEvent$),
            filter((time) => time > InactivityService.TIME_TO_RELOAD),
            take(1),
            switchMap(() => merge(of(null), fromEvent(this.window.nativeWindow.document, 'visibilitychange'))),
            filter(() => this.window.nativeWindow.document.visibilityState === 'visible'),
          )
          .subscribe(() => this.window.nativeWindow.location.reload()),
      );
    });
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
